import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useNavigateBack } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useSparePurchaseOrder, SparePurchaseOrderLine, SparePurchaseOrderExpense } from './useData';
import { moduleConfig } from './sparePurchaseOrdersModuleConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { Accordion, AccordionItem, Button, Callout, Card, ChevronLeftIcon, CountChip } from '../../../components/ui';

import { fetcher } from '../../../services/fetcher';
import { supplyBlockedActionMessage } from '../blockingMessages';

const fmt2 = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const toFinite = (value: string | number | null | undefined): number => {
  const n = typeof value === 'number' ? value : value != null && value !== '' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : 0;
};

const computeUnitTotal = (line: SparePurchaseOrderLine): number => {
  const q = toFinite(line.qty);
  const p = toFinite(line.unitPrice);
  if (q !== 0 && p !== 0) return q * p;
  const t = toFinite(line.lineTotal);
  const d = toFinite(line.discount);
  const v = toFinite(line.vat);
  return t + d - v;
};

const computeCurrencyFromLineTotal = (lineTotal: string | number | null | undefined, rate: number): number =>
  toFinite(lineTotal) * rate;

const formatItemCell = (line: SparePurchaseOrderLine) => {
  const v = (line.itemVcode ?? '').trim() || '';
  const n = (line.itemDescr ?? '').trim() || '';
  return [v, n].filter(Boolean).join(' - ');
};

type POLineSums = {
  unitTotal: number;
  discount: number;
  vat: number;
  total: number;
  currencyTotal: number;
  quotationQty: number;
};

const buildLineSums = (lineItems: SparePurchaseOrderLine[], rate: number): POLineSums => {
  return lineItems.reduce<POLineSums>(
    (acc, line) => {
      acc.unitTotal += computeUnitTotal(line);
      acc.discount += toFinite(line.discount);
      acc.vat += toFinite(line.vat);
      acc.total += toFinite(line.lineTotal);
      acc.currencyTotal += computeCurrencyFromLineTotal(line.lineTotal, rate);
      acc.quotationQty += toFinite(line.quotationQty);
      return acc;
    },
    { unitTotal: 0, discount: 0, vat: 0, total: 0, currencyTotal: 0, quotationQty: 0 },
  );
};

type POExpenseSums = { value: number; vat: number; currencyTotal: number };

const buildExpenseSums = (expenses: SparePurchaseOrderExpense[], rate: number): POExpenseSums => {
  return expenses.reduce<POExpenseSums>(
    (acc, e) => {
      const val = toFinite(e.value);
      const vat = toFinite(e.vat);
      acc.value += val;
      acc.vat += vat;
      acc.currencyTotal += (val + vat) * rate;
      return acc;
    },
    { value: 0, vat: 0, currencyTotal: 0 },
  );
};

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const numericId = id ? Number(id) : NaN;
  const { item, isLoading, isError } = useSparePurchaseOrder(Number.isFinite(numericId) ? numericId : undefined);
  const { data: forwardingsRes } = useSWR<any>('/spare-forwardings?page=1&limit=1000', fetcher);
  const { data: deliveriesRes } = useSWR<any>('/spare-deliveries?page=1&limit=1000', fetcher);
  const { data: invoicesRes } = useSWR<any>('/spare-invoices?page=1&limit=1000', fetcher);
  const { colors } = useTheme();
  const goBack = useNavigateBack(moduleConfig.module.route.list);
  const formatDate = (s?: string | null) => (s ? formatDateGlobal(s) : '');
  const currentCaseId = item?.caseId ?? 0;
  const hasForwardingInCase = React.useMemo(() => {
    if (currentCaseId <= 0) return false;
    const rows = Array.isArray(forwardingsRes) ? forwardingsRes : forwardingsRes?.data ?? [];
    return rows.some((f: any) => Number(f?.caseId) === currentCaseId);
  }, [forwardingsRes, currentCaseId]);
  const hasDeliveryInCase = React.useMemo(() => {
    if (currentCaseId <= 0) return false;
    const rows = Array.isArray(deliveriesRes) ? deliveriesRes : deliveriesRes?.data ?? [];
    return rows.some((d: any) => Number(d?.caseId) === currentCaseId);
  }, [deliveriesRes, currentCaseId]);
  const hasInvoiceInCase = React.useMemo(() => {
    if (currentCaseId <= 0) return false;
    const rows = Array.isArray(invoicesRes) ? invoicesRes : invoicesRes?.data ?? [];
    return rows.some((i: any) => Number(i?.caseId) === currentCaseId);
  }, [invoicesRes, currentCaseId]);
  const editBlockedInCase = hasForwardingInCase || hasDeliveryInCase || hasInvoiceInCase;

  if (!Number.isFinite(numericId)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid id.</p>;
  }
  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center rounded-xl border border-dashed px-4 py-16', colors.border)}>
        <p className={cn('text-sm font-medium', colors.textSecondary)}>Loading purchase order…</p>
      </div>
    );
  }
  if (isError || !item) {
    return (
      <Card variant="outlined" className={cn('p-6 sm:p-8', colors.border, 'max-w-md')}>
        <p className={cn('text-sm', colors.textSecondary)}>This purchase order was not found or you no longer have access.</p>
        <Button type="button" variant="ghost" className="mt-4" onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4" />
          Back to list
        </Button>
      </Card>
    );
  }

  const lineCount = (item.lineItems || []).length;
  const expenseCount = (item.expenses || []).length;
  const supplierLabel = item.supplier
    ? item.supplier.code
      ? `${item.supplier.name} (${item.supplier.code})`
      : item.supplier.name
    : `Supplier #${item.supplierId}`;

  const currencyLabel = item.currency
    ? item.currency.symbol
      ? `${item.currency.code} (${item.currency.symbol}) — ${item.currency.name}`
      : `${item.currency.code} — ${item.currency.name}`
    : '';

  const rateN = toFinite(item.exchangeRate);
  const symbol = (item.currency?.symbol ?? '').trim();
  const currencyTotalCaption = symbol ? `(${symbol}) Total` : 'Total';
  const lineItems = item.lineItems || [];
  const expList = item.expenses || [];
  const lineSums = buildLineSums(lineItems, rateN);
  const expenseSums = buildExpenseSums(expList, rateN);
  const grandCurrencyTotal = lineSums.currencyTotal + expenseSums.currencyTotal;
  const orderCcySubtotal = lineSums.total + expenseSums.value + expenseSums.vat;
  return (
    <div className="space-y-6">
      <Card variant="outlined" className={cn('overflow-hidden border shadow-sm', colors.border)}>
        <div
          className={cn(
            'flex flex-col gap-4 border-b px-4 py-4 sm:px-6 sm:py-5',
            colors.border,
            'bg-gradient-to-b from-slate-50/80 to-white dark:from-slate-900/40 dark:to-slate-900/20',
          )}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('-ml-2 gap-1.5 self-start font-medium', colors.text)}
                onClick={goBack}
              >
                <ChevronLeftIcon className="h-4 w-4 opacity-80" />
                Back
              </Button>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              {editBlockedInCase ? (
                <button
                  type="button"
                  disabled
                  title="This purchase order cannot be edited because forwarding, delivery, or invoice exists in this spare case."
                  className={cn(
                    'inline-flex h-9 min-h-[2.25rem] shrink-0 cursor-not-allowed items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold opacity-60',
                    colors.primary,
                    colors.primaryText,
                  )}
                >
                  Edit
                </button>
              ) : (
                <Link
                  to={moduleConfig.module.route.edit(item.id)}
                  state={{ from: `${location.pathname}${location.search}` }}
                  className={cn(
                    'inline-flex h-9 min-h-[2.25rem] shrink-0 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition-all hover:opacity-95',
                    colors.primary,
                    colors.primaryText,
                  )}
                >
                  Edit
                </Link>
              )}
            </div>
          </div>
        </div>
      </Card>
      {editBlockedInCase ? (
        <Callout variant="danger" title="Cannot edit purchase order">{supplyBlockedActionMessage('PO_EDIT_CASE_LINKED_FLOW')}</Callout>
      ) : null}

      <Card variant="outlined" className={cn('p-4 shadow-sm sm:p-5', colors.border)}>
        <Accordion className="space-y-3">
          <AccordionItem
            title="Order details"
            description="Quotation, supplier, currency, exchange, case, and notes."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mt-2 pt-1 sm:pt-0 ">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 text-sm md:grid-cols-12">
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Order date</dt>
                  <dd className={cn('mt-1', colors.text)}>{formatDate(item.orderDate)}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Quotation</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.quotation?.id ? (
                      <Link
                        to={`/spares/supplier-quotations/${item.quotation.id}`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.quotation.ref || `Quotation #${item.quotation.id}`}
                      </Link>
                    ) : (
                      <span className="tabular-nums">#{item.quotationId}</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Supplier</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>{supplierLabel}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Reference</dt>
                  <dd className={cn('mt-1 break-words font-medium tabular-nums', colors.text)}>{item.ref || ''}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Currency</dt>
                  <dd className={cn('mt-1', colors.text)}>{currencyLabel}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Exchange rate</dt>
                  <dd className={cn('mt-1 tabular-nums', colors.text)}>
                    {item.exchangeRate != null && String(item.exchangeRate) !== '' ? String(item.exchangeRate) : ''}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Exchange date</dt>
                  <dd className={cn('mt-1', colors.text)}>{formatDate(item.exchangeDate)}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Spare case</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.spareCase?.id ? (
                      <Link
                        to={`/spares/spare-cases/${item.spareCase.id}/case-view`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.spareCase.ref || `Case #${item.spareCase.id}`}
                      </Link>
                    ) : item.caseId ? (
                      <span className="tabular-nums">#{item.caseId}</span>
                    ) : (
                      <span className={cn('italic', colors.textSecondary)}>—</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-6">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Payment terms</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>{item.paymentTerms || ''}</dd>
                </div>
                <div className="min-w-0 md:col-span-6 md:col-start-1">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Notes</dt>
                  <dd
                    className={cn('mt-1 min-h-[1.25rem] max-w-3xl whitespace-pre-wrap break-words', colors.text)}
                  >
                    {item.notes?.trim() ? item.notes : <span className={colors.textSecondary}>—</span>}
                  </dd>
                </div>
              </dl>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Expenses"
            description="Value and VAT in order currency; (value + VAT) × exchange rate for the converted total column."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              expenseCount > 0 ? (
                <CountChip className="mr-1.5">
                  {expenseCount} line{expenseCount === 1 ? '' : 's'}
                </CountChip>
              ) : null
            }
          >
            <div className="-mx-1 overflow-x-auto sm:mx-0 sm:px-0">
              <table className="min-w-full text-sm">
                <thead>
                  <tr
                    className={cn(
                      'border-b text-left text-xs font-medium uppercase tracking-wider',
                      colors.border,
                      'bg-slate-50/95 dark:bg-slate-800/50',
                    )}
                  >
                    <th className="min-w-[10rem] px-2 py-2 text-left">Description</th>
                    <th className="w-28 px-2 py-2 text-right">Value</th>
                    <th className="w-28 px-2 py-2 text-right">VAT</th>
                    <th className="w-32 px-2 py-2 text-right">{currencyTotalCaption}</th>
                  </tr>
                </thead>
                <tbody>
                  {expList.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={cn('px-2 py-8 text-center text-sm', colors.textSecondary)}>
                        No expenses on this purchase order.
                      </td>
                    </tr>
                  ) : (
                    expList.map((exp, i) => {
                      const v = toFinite(exp.value);
                      const vt = toFinite(exp.vat);
                      const ccy = (v + vt) * rateN;
                      return (
                        <tr
                          key={exp.id ?? `exp-${i}`}
                          className={cn(
                            'align-top border-b transition-colors last:border-0',
                            colors.border,
                            'hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                          )}
                        >
                          <td className="p-2 text-left">
                            <span className={colors.text}>{exp.description || ''}</span>
                          </td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(v)}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(vt)}</td>
                          <td className="p-2 text-right font-medium tabular-nums text-indigo-700 dark:text-indigo-300">
                            {fmt2(ccy)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {expList.length > 0 ? (
                  <tfoot>
                    <tr className={cn('border-t', colors.border, colors.surfaceHover)}>
                      <td
                        colSpan={1}
                        className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}
                      >
                        SUM
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Value sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(expenseSums.value)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>VAT sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(expenseSums.vat)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>
                          {currencyTotalCaption} sum
                        </div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right text-indigo-700 dark:text-indigo-300')}>
                          {fmt2(expenseSums.currencyTotal)}
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Line items"
            description="Columns match the edit form (including Quo qty); line total × exchange rate for the converted column."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              lineCount > 0 ? (
                <CountChip className="mr-1.5">
                  {lineCount} line{lineCount === 1 ? '' : 's'}
                </CountChip>
              ) : null
            }
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr
                    className={cn(
                      'border-b text-left text-xs font-medium uppercase tracking-wider',
                      colors.border,
                      'bg-slate-50/95 dark:bg-slate-800/50',
                    )}
                  >
                    <th className="w-10 px-2 py-2 text-left tabular-nums">#</th>
                    <th className="w-[10rem] min-w-[8rem] px-2 py-2 text-left">Level 1</th>
                    <th className="min-w-[12rem] px-2 py-2 text-left">Item</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Unit</th>
                    <th className="w-24 px-2 py-2 text-right">Qty</th>
                    <th className="w-24 px-2 py-2 text-right">Price</th>
                    <th className="w-24 px-2 py-2 text-right">Unit total</th>
                    <th className="w-24 px-2 py-2 text-right">Discount</th>
                    <th className="w-24 px-2 py-2 text-right">VAT</th>
                    <th className="w-24 px-2 py-2 text-right">Total</th>
                    <th className="w-32 px-2 py-2 text-right">{currencyTotalCaption}</th>
                    <th className="w-20 px-2 py-2 text-right">Quo qty</th>
                    <th className="min-w-[10rem] px-2 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr>
                      <td colSpan={13} className={cn('px-2 py-8 text-center text-sm', colors.textSecondary)}>
                        No line items on this purchase order.
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((line, idx) => {
                      const ut = computeUnitTotal(line);
                      const d = toFinite(line.discount);
                      const v = toFinite(line.vat);
                      const t = toFinite(line.lineTotal);
                      const ccy = computeCurrencyFromLineTotal(line.lineTotal, rateN);
                      return (
                        <tr
                          key={line.id ?? `${line.itemId}-${line.itemDescr}`}
                          className={cn(
                            'align-top border-b transition-colors last:border-0',
                            colors.border,
                            'hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                          )}
                        >
                          <td className="p-2 text-left tabular-nums">{idx + 1}</td>
                          <td className="p-2 text-left">{(line.item?.level1 ?? '').trim() || ''}</td>
                          <td className="p-2 text-left">{formatItemCell(line)}</td>
                          <td className="p-2 text-right">{(line.unit?.ucode ?? '').trim() || ''}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(toFinite(line.qty))}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(toFinite(line.unitPrice))}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(ut)}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(d)}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(v)}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(t)}</td>
                          <td className="p-2 text-right font-semibold tabular-nums text-indigo-700 dark:text-indigo-300">
                            {fmt2(ccy)}
                          </td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(toFinite(line.quotationQty))}</td>
                          <td className="p-2 text-left">{line.itemNotes || ''}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {lineItems.length > 0 ? (
                  <tfoot>
                    <tr className={cn('border-t', colors.border)}>
                      <td colSpan={6} className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}>
                        SUM
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Unit total sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.unitTotal)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Discount sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.discount)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>VAT sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.vat)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Total sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.total)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>{currencyTotalCaption} sum</div>
                        <div
                          className={cn('text-sm font-semibold tabular-nums text-right text-indigo-700 dark:text-indigo-300')}
                        >
                          {fmt2(lineSums.currencyTotal)}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Quo qty sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.quotationQty)}</div>
                      </td>
                      <td className="px-2" />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </AccordionItem>
        </Accordion>

        {lineCount > 0 || expenseCount > 0 ? (
          <div
            className={cn(
              'mt-4 rounded-lg border px-4 py-3 text-sm sm:px-5',
              colors.border,
              'bg-slate-50/90 dark:bg-slate-800/30',
            )}
          >
            <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textSecondary)}>Order roll-up</h3>
            <dl className="mt-2 space-y-1.5 sm:grid sm:grid-cols-1 sm:gap-2 sm:space-y-0">
              {lineCount > 0 ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 sm:gap-4">
                  <dt className="text-slate-600 dark:text-slate-400">Lines subtotal (order currency)</dt>
                  <dd className="font-medium tabular-nums">{fmt2(lineSums.total)}</dd>
                </div>
              ) : null}
              {expenseCount > 0 ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 sm:gap-4">
                  <dt className="text-slate-600 dark:text-slate-400">Expenses (value + VAT, order currency)</dt>
                  <dd className="font-medium tabular-nums">{fmt2(expenseSums.value + expenseSums.vat)}</dd>
                </div>
              ) : null}
              {lineCount > 0 && expenseCount > 0 ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-dashed pt-2 sm:gap-4">
                  <dt className="font-medium text-slate-700 dark:text-slate-300">Combined (order currency)</dt>
                  <dd className="font-semibold tabular-nums">{fmt2(orderCcySubtotal)}</dd>
                </div>
              ) : null}
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-t pt-2 sm:gap-4">
                <dt className="font-medium text-slate-800 dark:text-slate-200">Grand total ({currencyTotalCaption}, lines + expenses)</dt>
                <dd className="text-base font-bold tabular-nums text-indigo-700 dark:text-indigo-300">{fmt2(grandCurrencyTotal)}</dd>
              </div>
            </dl>
            <p className={cn('mt-2 text-xs', colors.textSecondary)}>
              Line converted column: line total × exchange rate (same as edit). Expenses: (value + VAT) × exchange rate.
            </p>
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export default Detail;
